import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LocalNumberPipe } from '@app/shared/pipes/local-number.pipe';
import { EfficiencyTresholdService } from '@app/efficiency-analysis/services/efficiency-treshold.service';
import { AuthService } from '@app/shared/services/auth.service';
import { BuildingEfficiencyWizardStore } from '../building-efficiency-wizard.component.store';
import { IRawWizardData } from '@app/efficiency-analysis/models/raw-wizard-data';
import { ApplicationInsightEvent } from '@app/shared/enums';
import { AppInsightsServiceDirective } from '@app/shared/services/app-insights.service';
import { EfficiencyThreshold } from '@app/efficiency-analysis/models/thresholds/efficiency-threshold';
import { isNil } from 'lodash';
import { filter, takeUntil } from 'rxjs/operators';
import { DotDayMonthYearFormat } from '@app/shared/constants';
import { DateUtilities } from '@app/shared/date-utility';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-building-efficiency-results',
  templateUrl: './building-efficiency-results.component.html',
  styleUrls: ['./building-efficiency-results.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class BuildingEfficiencyResultsComponent implements OnInit, OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  @Input() isEditable = true;

  chart1: BuildingEfficiencyChartProperties = {} as BuildingEfficiencyChartProperties;
  chart2: BuildingEfficiencyChartProperties = {} as BuildingEfficiencyChartProperties;
  electricityDataRange: PowerLimits = {} as PowerLimits;
  heatDataRange: PowerLimits = {} as PowerLimits;
  electricityData: PowerDetails[] = [];
  heatData: PowerDetails[] = [];
  marker = `./assets/icons/primary-indicator.svg`;
  strom = `./assets/icons/strom-icon.svg`;
  warme = `./assets/icons/warme-icon.svg`;
  notes: string = '';
  noteLimit = 250;
  noteLimitText = '';

  widthAdjustment = 70;
  electricityColorStatus?: string;
  heatColorStatus?: string;
  data: IRawWizardData = {} as IRawWizardData;
  startDate?: string;
  endDate?: string;

  stromChartName = 'electricity';
  warmeChartName = 'heat';

  constructor(
    public translate: TranslateService,
    private localNumberPipe: LocalNumberPipe,
    private efficiencyTreshold: EfficiencyTresholdService,
    public authService: AuthService,
    private appInsightsServiceDirective: AppInsightsServiceDirective,
    private _buildingEfficiencyWizardStore: BuildingEfficiencyWizardStore
  ) {}

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public ngOnInit(): void {
    this._buildingEfficiencyWizardStore.wizardData$
      .pipe(
        filter((wizard: IRawWizardData | null): wizard is IRawWizardData => !isNil(wizard)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((data: IRawWizardData) => {
        this.data = data;
        this.initializeBuildingEfficiencyResults();
        this.logBEResults();
      });
  }

  public getLocalNumber(value: any): any {
    const localNumberValue = value < 1 ? this.localNumberPipe.transform(value) : this.localNumberPipe.transform(parseInt(value.toFixed(0), 10)).split(',')[0];
    return localNumberValue;
  }

  public notesChange(): void {
    this.data.buildingEfficiencyData.notes = this.notes;
  }

  private initializeBuildingEfficiencyResults(): void {
    this.chart1 = {
      id: 'electricity',
      name: this.translate.instant('module.shared.buildingEfficiencyWizard.step4.electricityChartNameText'),
    };

    this.chart2 = {
      id: 'heat',
      name: this.translate.instant('module.shared.buildingEfficiencyWizard.step4.heatChartNameText'),
    };

    this.startDate = this.data.hasTimePeriod ? this.getDisplayDate(this.data.userDefinedStartDate) : this.getDisplayDate(this.data.defaultStartDate);
    this.endDate = this.data.hasTimePeriod ? this.getDisplayDate(this.data.userDefinedEndDate) : this.getDisplayDate(this.data.defaultEndDate);

    this.notes = this.data.buildingEfficiencyData.notes || '';

    this.electricityDataRange = {
      minValue: this.data.buildingEfficiencyData.electricityMinDataRange,
      maxValue: Math.round(this.data.buildingEfficiencyData.electricityMaxDataRange / 10) * 10,
    };
    this.heatDataRange = {
      minValue: this.data.buildingEfficiencyData.heatMinDataRange,
      maxValue: Math.round(this.data.buildingEfficiencyData.heatMaxDataRange / 10) * 10,
    };

    this.electricityData = [
      {
        indicator: 'lightGrey',
        value: this.data.buildingEfficiencyData.averageElectricityConsumption,
        unit: 'kWh/(m²a)',
      },
      {
        indicator: 'darkGrey',
        value: this.data.buildingEfficiencyData.electricityGuideValue,
        unit: 'kWh/(m²a)',
      },
      {
        indicator: 'companyColor',
        value: this.data.buildingEfficiencyData.electricityComputedValue,
        unit: 'kWh/(m²a)',
      },
    ];
    this.heatData = [
      {
        indicator: 'lightGrey',
        value: this.data.buildingEfficiencyData.averageHeatConsumption,
        unit: 'kWh/(m²a)',
      },
      {
        indicator: 'darkGrey',
        value: this.data.buildingEfficiencyData.heatGuideValue,
        unit: 'kWh/(m²a)',
      },
      {
        indicator: 'companyColor',
        value: this.data.buildingEfficiencyData.heatComputedValue,
        unit: 'kWh/(m²a)',
      },
    ];

    this.efficiencyTreshold.treshold.pipe(takeUntil(this.unsubscribe$)).subscribe((threshold: EfficiencyThreshold) => {
      this.electricityColorStatus = this.getColorStatus(
        this.data.buildingEfficiencyData.efficiencyPercentageElectricity,
        threshold.efficientTreshold,
        threshold.inefficientTreshold
      );
      this.heatColorStatus = this.getColorStatus(
        this.data.buildingEfficiencyData.efficiencyPercentageHeat,
        threshold.efficientTreshold,
        threshold.inefficientTreshold
      );
    });
  }

  private getColorStatus(percentage: number, efficientTreshold: number, inefficientTreshold: number): string {
    if (percentage >= 0 && percentage <= efficientTreshold) {
      return 'green';
    } else if (efficientTreshold < percentage && percentage < inefficientTreshold) {
      return 'yellow';
    }
    //if ((this.inefficient <= value && value <= 100) || value > 100)
    return 'red';
  }

  private getDisplayDate(date: string | Date): string {
    return DateUtilities.toExpectedFormat(date, DotDayMonthYearFormat);
  }

  private logBEResults(): void {
    this.appInsightsServiceDirective.logEvent(ApplicationInsightEvent.viewEfficiencyAnalysis, {
      objectType: this.data.buildingEfficiencyData.buildingTypeName,
      data: this.data,
      warmeStatus: this.data.buildingEfficiencyData.heatColorStatus,
      stromStatus: this.data.buildingEfficiencyData.electricityColorStatus,
    });
  }
}

interface PowerDetails {
  indicator: string;
  value: number;
  unit: string;
}

interface PowerLimits {
  minValue: number;
  maxValue: number;
}

interface BuildingEfficiencyChartProperties {
  id: string;
  name: string;
}
